/**
 * Accommodation-Day Integration Utilities
 * 
 * This module provides utility functions for managing the integration between
 * accommodations and day entries, including date range calculations and content formatting.
 * 
 * Phase 2A: Core Day Integration utilities
 */

/**
 * Calculates the date range for accommodation day entries
 * @param {Object} accommodation - The accommodation object
 * @returns {Object|null} Date range object or null if invalid
 */
function calculateAccommodationDateRange(accommodation) {
  const checkInDate = accommodation?.details?.checkInDate;
  const checkOutDate = accommodation?.details?.checkOutDate;

  if (!checkInDate) {
    return null;
  }

  const checkIn = new Date(checkInDate);
  const checkOut = checkOutDate ? new Date(checkOutDate) : null;

  // Validate dates
  if (isNaN(checkIn.getTime())) {
    return null;
  }

  if (checkOut && (isNaN(checkOut.getTime()) || checkOut < checkIn)) {
    return null;
  }

  // Single date handling
  if (!checkOut || checkOut.getTime() === checkIn.getTime()) {
    return {
      startDate: checkInDate,
      endDate: checkInDate,
      duration: 1
    };
  }

  // Multi-day stay - exclude check-out day from stay
  // Use UTC-safe date arithmetic to prevent timezone/DST shifts
  const addDaysIsoUtc = (isoDate, daysOffset) => {
    const date = new Date(isoDate + 'T00:00:00.000Z'); // Force UTC interpretation
    date.setUTCDate(date.getUTCDate() + daysOffset);
    return date.toISOString().split('T')[0];
  };

  const stayEndDate = addDaysIsoUtc(checkOutDate, -1); // Day before check-out
  const duration = Math.ceil((new Date(stayEndDate) - checkIn) / (1000 * 60 * 60 * 24)) + 1;

  return {
    startDate: checkInDate,
    endDate: stayEndDate,
    duration: duration
  };
}

/**
 * Formats accommodation content for day entry display
 * @param {Object} accommodation - The accommodation object
 * @param {string} date - The date to format for
 * @returns {string} Formatted content string
 */
function formatAccommodationContentForDay(accommodation, date) {
  const name = accommodation.details?.name || 'Accommodation';
  const city = accommodation.details?.city;
  const country = accommodation.details?.country;
  const checkInDate = accommodation.details?.checkInDate;
  const checkOutDate = accommodation.details?.checkOutDate;

  // Build location string
  let location = '';
  if (city && country) {
    location = `${city}, ${country}`;
  } else if (city) {
    location = city;
  }

  // Base content format
  let content = `Stay at ${name}`;
  if (location) {
    content += `: ${location}`;
  }

  // Add context if dates are available
  if (checkInDate && checkOutDate) {
    if (date === checkInDate && date === checkOutDate) {
      // Single day stay - no context needed
      return content;
    } else if (date === checkInDate) {
      content += ' (Check-in)';
    } else if (date === checkOutDate) {
      content += ' (Check-out)';
    } else {
      content += ' (Staying)';
    }
  } else if (checkInDate && date === checkInDate) {
    content += ' (Check-in)';
  }

  return content;
}

/**
 * Validates accommodation dates for logical consistency
 * @param {string} checkInDate - Check-in date
 * @param {string} checkOutDate - Check-out date (optional)
 * @returns {Object} Validation result
 */
function validateAccommodationDates(checkInDate, checkOutDate) {
  const result = {
    isValid: true,
    duration: 1,
    error: null
  };

  if (!checkInDate) {
    result.isValid = false;
    result.error = 'Check-in date is required';
    return result;
  }

  const checkIn = new Date(checkInDate);
  if (isNaN(checkIn.getTime())) {
    result.isValid = false;
    result.error = 'Invalid date format';
    return result;
  }

  if (checkOutDate) {
    const checkOut = new Date(checkOutDate);
    if (isNaN(checkOut.getTime())) {
      result.isValid = false;
      result.error = 'Invalid date format';
      return result;
    }

    if (checkOut < checkIn) {
      result.isValid = false;
      result.error = 'Check-out date must be after check-in date';
      return result;
    }

    // Calculate duration
    const daysDiff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    if (daysDiff > 90) {
      result.isValid = false;
      result.error = 'Stay duration exceeds maximum allowed (90 days)';
      return result;
    }

    result.duration = daysDiff;
  }

  return result;
}

/**
 * Checks if accommodation content already exists in day content
 * @param {string} dayContent - Existing day content
 * @param {string} accommodationName - Accommodation name to check
 * @returns {boolean} True if content exists
 */
function accommodationContentExists(dayContent, accommodationName) {
  if (!dayContent || !accommodationName) {
    return false;
  }

  // Look for "Stay at [AccommodationName]" pattern
  const pattern = new RegExp(`Stay at[^\\n]*${accommodationName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
  return pattern.test(dayContent);
}

/**
 * Extracts accommodation references from day content
 * @param {string} dayContent - Day content to analyze
 * @returns {Array} Array of accommodation reference objects
 */
function extractAccommodationReferences(dayContent) {
  if (!dayContent) {
    return [];
  }

  // Pattern: "Stay at [Name]: [City] (Context)"
  const pattern = /Stay at ([^:]+?)(?:: ([^\n(]+?))?(?:\s*\(([^)]+)\))?(?=\n|$)/g;
  const references = [];
  let match;

  while ((match = pattern.exec(dayContent)) !== null) {
    references.push({
      accommodationName: match[1].trim(),
      city: match[2] ? match[2].trim() : null,
      fullMatch: match[0],
      context: match[3] ? match[3].trim() : null
    });
  }

  return references;
}

/**
 * Removes accommodation content from day content
 * @param {string} dayContent - Original day content
 * @param {string} accommodationName - Accommodation name to remove
 * @returns {Object} Result with updated content and removal info
 */
function removeAccommodationContentFromDay(dayContent, accommodationName) {
  if (!dayContent || !accommodationName) {
    return {
      content: dayContent,
      removed: false,
      removedLine: null
    };
  }

  const lines = dayContent.split('\n');
  const pattern = new RegExp(`Stay at[^\\n]*${accommodationName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');

  let removedLine = null;
  const filteredLines = lines.filter(line => {
    if (pattern.test(line)) {
      removedLine = line;
      return false;
    }
    return true;
  });

  return {
    content: filteredLines.join('\n'),
    removed: removedLine !== null,
    removedLine: removedLine
  };
}

/**
 * Generates a unique link ID for accommodation-day relationships
 * @param {string} accommodationId - Accommodation ID
 * @param {string} dayId - Day ID
 * @returns {string} Generated link ID
 */
function generateAccommodationDayLinkId(accommodationId, dayId) {
  // Create a deterministic but unique ID
  const timestamp = Date.now().toString(36);
  return `link_${accommodationId}_${dayId}_${timestamp}`;
}

/**
 * Date Change Detection Utilities
 * Supporting accommodation modification workflows for existing day entries
 */

/**
 * Calculates an array of date strings between start and end dates (inclusive).
 * Used for determining which days are affected by accommodation date changes.
 * For accommodations, the checkout date is exclusive (you don't stay on checkout day).
 * 
 * @param {string|Date} startDate - Start date (inclusive)
 * @param {string|Date} endDate - End date (exclusive for accommodation checkout)
 * @returns {Array<string>} Array of date strings in YYYY-MM-DD format
 */
function calculateDateRange(startDate, endDate) {
  if (!startDate || !endDate) {
    return [];
  }

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('calculateDateRange: Invalid date provided', { startDate, endDate });
      return [];
    }

    // For accommodation, checkout date is exclusive (you don't stay on checkout day)
    // So we calculate days from check-in up to (but not including) check-out
    // Use UTC-safe date arithmetic to prevent timezone/DST shifts
    const dates = [];

    const addDaysIsoUtc = (isoDate, daysOffset) => {
      const date = new Date(isoDate + 'T00:00:00.000Z'); // Force UTC interpretation
      date.setUTCDate(date.getUTCDate() + daysOffset);
      return date.toISOString().split('T')[0];
    };

    const startIsoDate = start.toISOString().split('T')[0];
    const endIsoDate = end.toISOString().split('T')[0];
    let currentIsoDate = startIsoDate;

    while (currentIsoDate < endIsoDate) {
      dates.push(currentIsoDate);
      currentIsoDate = addDaysIsoUtc(currentIsoDate, 1);
    }

    return dates;
  } catch (error) {
    console.error('calculateDateRange: Error calculating date range:', error);
    return [];
  }
}

/**
 * Analyzes changes between old and new accommodation date ranges
 * @param {Object} oldDetails - Previous accommodation details
 * @param {Object} newDetails - Updated accommodation details
 * @returns {Object} Analysis of date changes with type and affected ranges
 */
function analyzeDateRangeChanges(oldDetails, newDetails) {
  try {
    const oldCheckIn = oldDetails?.checkInDate;
    const oldCheckOut = oldDetails?.checkOutDate;
    const newCheckIn = newDetails?.checkInDate;
    const newCheckOut = newDetails?.checkOutDate;

    // Handle null/undefined dates as empty strings for consistent comparison
    const normalizeDate = (date) => date || '';

    const oldStart = normalizeDate(oldCheckIn);
    const oldEnd = normalizeDate(oldCheckOut);
    const newStart = normalizeDate(newCheckIn);
    const newEnd = normalizeDate(newCheckOut);

    // Determine change type
    const hadDates = oldStart || oldEnd;
    const hasDates = newStart || newEnd;

    if (!hadDates && !hasDates) {
      return { type: 'NO_CHANGE', hasChanges: false };
    }

    if (!hadDates && hasDates) {
      return {
        type: 'DATES_ADDED',
        hasChanges: true,
        newRange: { checkInDate: newStart, checkOutDate: newEnd },
        affectedDays: newStart ? calculateDateRange(newStart, newEnd) : []
      };
    }

    if (hadDates && !hasDates) {
      return {
        type: 'DATES_REMOVED',
        hasChanges: true,
        oldRange: { checkInDate: oldStart, checkOutDate: oldEnd },
        affectedDays: oldStart ? calculateDateRange(oldStart, oldEnd) : []
      };
    }

    // Both had dates - analyze the change
    if (oldStart === newStart && oldEnd === newEnd) {
      return { type: 'NO_CHANGE', hasChanges: false };
    }

    const oldDays = oldStart ? calculateDateRange(oldStart, oldEnd) : [];
    const newDays = newStart ? calculateDateRange(newStart, newEnd) : [];

    // Calculate overlapping, added, and removed days
    const oldDaysSet = new Set(oldDays);
    const newDaysSet = new Set(newDays);

    const removedDays = oldDays.filter(day => !newDaysSet.has(day));
    const addedDays = newDays.filter(day => !oldDaysSet.has(day));
    const overlappingDays = oldDays.filter(day => newDaysSet.has(day));

    // Determine specific change type
    let changeType;
    if (removedDays.length === 0 && addedDays.length > 0) {
      changeType = 'EXPANSION';
    } else if (addedDays.length === 0 && removedDays.length > 0) {
      changeType = 'CONTRACTION';
    } else if (overlappingDays.length === 0) {
      changeType = 'COMPLETE_SHIFT';
    } else if (oldDays.length === newDays.length) {
      changeType = 'SHIFT_SAME_DURATION';
    } else {
      changeType = 'SHIFT_DIFFERENT_DURATION';
    }

    return {
      type: changeType,
      hasChanges: true,
      oldRange: { checkInDate: oldStart, checkOutDate: oldEnd },
      newRange: { checkInDate: newStart, checkOutDate: newEnd },
      oldDays,
      newDays,
      addedDays,
      removedDays,
      overlappingDays
    };

  } catch (error) {
    console.error('Error analyzing date range changes:', error);
    return {
      type: 'ERROR',
      hasChanges: false,
      error: error.message
    };
  }
}

/**
 * Determines if a date change represents a date range contraction.
 * A contraction occurs when the new accommodation dates cover fewer days
 * than the original dates, requiring removal of day entries for the excluded days.
 * 
 * @param {Object} changeAnalysis - Result from analyzeDateRangeChanges
 * @returns {boolean} True if this is a date range contraction
 */
function isContractionScenario(changeAnalysis) {
  return changeAnalysis?.type === 'CONTRACTION' &&
    changeAnalysis?.removedDays?.length > 0;
}

/**
 * Determines if a date change represents a date range expansion.
 * An expansion occurs when the new accommodation dates cover more days
 * than the original dates, requiring addition of day entries for the new days.
 * 
 * @param {Object} changeAnalysis - Result from analyzeDateRangeChanges
 * @returns {boolean} True if this is a date range expansion
 */
function isExpansionScenario(changeAnalysis) {
  return changeAnalysis?.type === 'EXPANSION' &&
    changeAnalysis?.addedDays?.length > 0;
}

/**
 * Determines if a date change represents a date range shift.
 * A shift occurs when accommodation dates move to different time periods,
 * which may involve both adding new day entries and removing old ones.
 * Shifts can maintain the same duration or change duration while moving dates.
 * 
 * @param {Object} changeAnalysis - Result from analyzeDateRangeChanges
 * @returns {Object} Analysis of shift type and complexity
 */
function analyzeShiftScenario(changeAnalysis) {
  if (!changeAnalysis?.type?.includes('SHIFT')) {
    return { isShift: false };
  }

  const hasOverlap = changeAnalysis.overlappingDays?.length > 0;
  const sameDuration = changeAnalysis.type === 'SHIFT_SAME_DURATION';

  return {
    isShift: true,
    hasOverlap,
    sameDuration,
    complexity: hasOverlap ? 'PARTIAL_OVERLAP' : 'COMPLETE_SHIFT',
    shiftType: sameDuration ? 'SAME_DURATION' : 'DIFFERENT_DURATION'
  };
}

/**
 * Formats accommodation content for day entries (simple version for suggestions)
 * @param {string} accommodationName - Name of the accommodation
 * @param {string} city - City of the accommodation
 * @returns {string} Formatted content string
 */
function formatAccommodationContent(accommodationName, city) {
  const name = accommodationName || 'Accommodation';
  const location = city ? `: ${city}` : '';
  return `Stay at ${name}${location}`;
}

/**
 * Generates suggestions for date range changes based on change analysis
 * @param {Object} changeAnalysis - Result from analyzeDateRangeChanges
 * @param {Object} accommodation - Accommodation data
 * @returns {Array} Array of suggestion objects for different types of changes
 */
function generateDateChangeSuggestions(changeAnalysis, accommodation) {
  const suggestions = [];

  try {
    if (!changeAnalysis?.hasChanges) {
      return suggestions;
    }

    const accommodationName = accommodation?.details?.name || 'Unnamed Accommodation';
    const city = accommodation?.details?.city || '';

    switch (changeAnalysis.type) {
      case 'DATES_ADDED':
        // Adding dates to previously undated accommodation
        suggestions.push({
          type: 'ADD_DAY_ENTRIES',
          days: changeAnalysis.affectedDays,
          content: formatAccommodationContent(accommodationName, city),
          description: `Add accommodation entries for ${changeAnalysis.affectedDays.length} day(s)`
        });
        break;

      case 'DATES_REMOVED':
        // Removing dates from previously dated accommodation
        suggestions.push({
          type: 'REMOVE_DAY_ENTRIES',
          days: changeAnalysis.affectedDays,
          description: `Remove accommodation entries from ${changeAnalysis.affectedDays.length} day(s)`,
          requiresConfirmation: true
        });
        break;

      case 'EXPANSION':
        // Expanding accommodation date range
        suggestions.push({
          type: 'ADD_DAY_ENTRIES',
          days: changeAnalysis.addedDays,
          content: formatAccommodationContent(accommodationName, city),
          description: `Add accommodation entries for ${changeAnalysis.addedDays.length} additional day(s)`
        });
        break;

      case 'CONTRACTION':
        // Contracting accommodation date range
        suggestions.push({
          type: 'REMOVE_DAY_ENTRIES',
          days: changeAnalysis.removedDays,
          description: `Remove accommodation entries from ${changeAnalysis.removedDays.length} day(s)`,
          requiresConfirmation: true
        });
        break;

      case 'SHIFT_SAME_DURATION':
      case 'SHIFT_DIFFERENT_DURATION':
        // Date shifts with potential overlapping periods
        if (changeAnalysis.removedDays.length > 0) {
          suggestions.push({
            type: 'REMOVE_DAY_ENTRIES',
            days: changeAnalysis.removedDays,
            description: `Remove old accommodation entries from ${changeAnalysis.removedDays.length} day(s)`,
            requiresConfirmation: true
          });
        }

        if (changeAnalysis.addedDays.length > 0) {
          suggestions.push({
            type: 'ADD_DAY_ENTRIES',
            days: changeAnalysis.addedDays,
            content: formatAccommodationContent(accommodationName, city),
            description: `Add accommodation entries for ${changeAnalysis.addedDays.length} new day(s)`
          });
        }

        // Handle overlapping days that need content updates
        if (changeAnalysis.overlappingDays.length > 0) {
          suggestions.push({
            type: 'UPDATE_DAY_ENTRIES',
            days: changeAnalysis.overlappingDays,
            content: formatAccommodationContent(accommodationName, city),
            description: `Update accommodation entries for ${changeAnalysis.overlappingDays.length} overlapping day(s)`
          });
        }
        break;

      case 'COMPLETE_SHIFT':
        // Complete shift to different time period with no overlap
        suggestions.push({
          type: 'REMOVE_DAY_ENTRIES',
          days: changeAnalysis.removedDays,
          description: `Remove old accommodation entries from ${changeAnalysis.removedDays.length} day(s)`,
          requiresConfirmation: true
        });

        suggestions.push({
          type: 'ADD_DAY_ENTRIES',
          days: changeAnalysis.addedDays,
          content: formatAccommodationContent(accommodationName, city),
          description: `Add accommodation entries for ${changeAnalysis.addedDays.length} new day(s)`
        });
        break;

      default:
        console.warn('Unhandled date change type:', changeAnalysis.type);
    }

  } catch (error) {
    console.error('Error generating date change suggestions:', error);
  }

  return suggestions;
}

/**
 * Validates that date changes are logical and safe to process
 * @param {Object} oldDetails - Previous accommodation details
 * @param {Object} newDetails - Updated accommodation details
 * @returns {Object} Validation result with any errors or warnings
 */
function validateDateChanges(oldDetails, newDetails) {
  const errors = [];
  const warnings = [];

  try {
    const newCheckIn = newDetails?.checkInDate;
    const newCheckOut = newDetails?.checkOutDate;

    // Check if both dates exist and check-in is before check-out
    if (newCheckIn && newCheckOut) {
      const checkInDate = new Date(newCheckIn);
      const checkOutDate = new Date(newCheckOut);

      if (checkInDate >= checkOutDate) {
        errors.push('Check-in date must be before check-out date');
      }

      // Warn about very long stays (more than 30 days)
      const daysDiff = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      if (daysDiff > 30) {
        warnings.push(`Long stay detected: ${daysDiff} days. Please verify dates are correct.`);
      }
    }

    // Check for invalid date formats
    if (newCheckIn && isNaN(new Date(newCheckIn).getTime())) {
      errors.push('Invalid check-in date format');
    }

    if (newCheckOut && isNaN(new Date(newCheckOut).getTime())) {
      errors.push('Invalid check-out date format');
    }

  } catch (error) {
    errors.push(`Date validation error: ${error.message}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

// ====================================================================
// ACCOMMODATION-DAYS CREATION UTILITIES
// ====================================================================
// The following functions are designed for creating new day entry suggestions
// from accommodation data. They handle single check-in dates, date ranges,
// missing day detection, and validation for day creation workflows.
// These complement the existing functions which handle accommodation modifications.

/**
 * Calculates the date range from accommodation check-in/check-out dates for day entry suggestions.
 * This function processes accommodation dates to determine which days should have accommodation entries
 * in the user's daily itinerary, handling both single check-in dates and full stay periods.
 * 
 * The calculation follows accommodation industry standards where guests typically stay until
 * check-out day but don't occupy the room on check-out day itself, so day entries are created
 * for all nights stayed (check-in through the day before check-out).
 * 
 * @param {Object} accommodation - The accommodation object containing date information
 * @param {Object} accommodation.details - Accommodation details object
 * @param {string} accommodation.details.checkInDate - ISO date string for check-in
 * @param {string} [accommodation.details.checkOutDate] - ISO date string for check-out (optional)
 * @returns {Object|null} Date range information or null if invalid
 * @returns {string[]} returns.dates - Array of ISO date strings for days needing entries
 * @returns {string} returns.stayType - Type of stay: 'SINGLE_DAY', 'SAME_DAY_CHECKIN_CHECKOUT', or 'MULTI_DAY'
 * @returns {number} returns.totalDays - Number of days in the stay period
 * @returns {boolean} returns.requiresContextualContent - Whether different days need different content types
 */
function calculateAccommodationDateRangeForDaysSuggestions(accommodation) {
  const checkInDate = accommodation?.details?.checkInDate;
  const checkOutDate = accommodation?.details?.checkOutDate;

  if (!checkInDate) {
    return null;
  }

  const checkIn = new Date(checkInDate);

  // Validate check-in date
  if (isNaN(checkIn.getTime())) {
    return null;
  }

  // Single date stay - check-in only
  if (!checkOutDate) {
    return {
      dates: [checkInDate],
      stayType: 'SINGLE_DAY',
      totalDays: 1,
      requiresContextualContent: false,
      startDate: checkInDate,
      endDate: checkInDate
    };
  }

  const checkOut = new Date(checkOutDate);

  // Validate check-out date
  if (isNaN(checkOut.getTime()) || checkOut < checkIn) {
    return null;
  }

  // Same day check-in and check-out (single day stay)
  if (checkOut.getTime() === checkIn.getTime()) {
    return {
      dates: [checkInDate],
      stayType: 'SAME_DAY_CHECKIN_CHECKOUT',
      totalDays: 1,
      requiresContextualContent: false,
      startDate: checkInDate,
      endDate: checkInDate
    };
  }

  // Multi-day stay - exclude check-out day from accommodation stay period
  // Use UTC-safe date arithmetic to prevent timezone/DST shifts
  const dates = [];

  // Helper function for UTC-safe date arithmetic
  const addDaysIsoUtc = (isoDate, daysOffset) => {
    // Normalize input - extract date-only portion if full datetime is passed
    let dateOnly;
    if (typeof isoDate === 'string') {
      dateOnly = isoDate.split('T')[0]; // Extract date portion if datetime
    } else if (isoDate instanceof Date) {
      dateOnly = isoDate.toISOString().split('T')[0];
    } else {
      console.warn('addDaysIsoUtc received unexpected type:', typeof isoDate, isoDate);
      return null;
    }

    // Validate the date string format
    if (!dateOnly || !/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      console.warn('addDaysIsoUtc received invalid date format:', dateOnly);
      return null;
    }

    const date = new Date(dateOnly + 'T00:00:00.000Z'); // Force UTC interpretation
    if (isNaN(date.getTime())) {
      console.warn('addDaysIsoUtc: Invalid date after parsing:', dateOnly);
      return null;
    }
    date.setUTCDate(date.getUTCDate() + daysOffset);
    return date.toISOString().split('T')[0];
  };

  // Calculate dates between check-in and day before check-out
  let currentIsoDate = checkInDate.split('T')[0]; // Start with normalized check-in date
  const lastStayIsoDate = addDaysIsoUtc(checkOutDate, -1); // Day before check-out

  // Handle invalid dates gracefully
  if (!lastStayIsoDate) {
    console.warn('calculateAccommodationDateRangeForDaysSuggestions: Could not calculate last stay date from:', checkOutDate);
    return null;
  }

  while (currentIsoDate && currentIsoDate <= lastStayIsoDate) {
    dates.push(currentIsoDate);
    currentIsoDate = addDaysIsoUtc(currentIsoDate, 1);
    // Safety break if addDaysIsoUtc fails
    if (!currentIsoDate) {
      console.warn('calculateAccommodationDateRangeForDaysSuggestions: Date increment failed, breaking loop');
      break;
    }
  }

  return {
    dates,
    stayType: 'MULTI_DAY',
    totalDays: dates.length,
    requiresContextualContent: dates.length > 1,
    startDate: dates[0],
    endDate: dates[dates.length - 1]
  };
}

/**
 * Determines the contextual role of a specific date within an accommodation stay period.
 * This function analyzes where a given date falls within the accommodation's stay timeline
 * to determine what type of day entry content should be generated (arrival, departure, or staying).
 * 
 * The context determination helps create more informative day entries by distinguishing between:
 * - Check-in days: When guests arrive and need arrival-related information
 * - Check-out days: When guests depart and need departure-related information  
 * - Staying days: Regular nights when guests are simply staying at the accommodation
 * - Single-day stays: When check-in and check-out occur on the same day
 * 
 * This contextual information enables the system to generate appropriate day entry content
 * such as "Stay at Hotel: Paris (Check-in)" vs "Stay at Hotel: Paris" vs "Stay at Hotel: Paris (Check-out)".
 * 
 * @param {Object} accommodationDetails - Accommodation details containing date information
 * @param {string} accommodationDetails.checkInDate - ISO date string for check-in
 * @param {string} [accommodationDetails.checkOutDate] - ISO date string for check-out (optional)
 * @param {string} date - ISO date string for the specific date to analyze
 * @param {string[]} allDatesInRange - Array of all dates in the accommodation stay period
 * @returns {string} Context type: 'check-in', 'staying', 'check-out', or 'single-day'
 */
function determineAccommodationStayContext(accommodationDetails, date, allDatesInRange) {
  if (!accommodationDetails || !date || !Array.isArray(allDatesInRange)) {
    return 'single-day';
  }

  const checkInDate = accommodationDetails.checkInDate;
  const checkOutDate = accommodationDetails.checkOutDate;

  // Single day stay
  if (allDatesInRange.length === 1) {
    return 'single-day';
  }

  // Multi-day stay logic
  if (checkInDate && checkOutDate) {
    if (date === checkInDate && date === checkOutDate) {
      // Same day check-in and check-out
      return 'single-day';
    } else if (date === checkInDate) {
      return 'check-in';
    } else if (date === allDatesInRange[allDatesInRange.length - 1]) {
      // Last day of stay (day before check-out)
      return 'check-out';
    } else {
      return 'staying';
    }
  } else if (checkInDate && date === checkInDate) {
    // Only check-in date provided
    return 'check-in';
  }

  return 'staying';
}

/**
 * Identifies which days in an accommodation's date range don't have corresponding day entries.
 * This function cross-references the accommodation's stay period with existing day entries
 * to determine where gaps exist in the user's daily itinerary coverage.
 * 
 * The function is essential for accommodation-to-days integration because users may:
 * - Add accommodations before creating their daily itinerary
 * - Have partial day coverage that doesn't match their accommodation dates
 * - Need to be informed about missing day entries to maintain complete trip planning
 * 
 * When missing days are detected, the system can:
 * - Suggest creating day entries for the missing dates
 * - Provide informational notifications about incomplete coverage
 * - Allow users to decide whether to create the missing day entries
 * 
 * The function handles service failures gracefully by treating inaccessible days as missing,
 * ensuring the system continues to function even when the days service is temporarily unavailable.
 * 
 * @param {string} userId - User identifier for accessing day entries
 * @param {string} tripId - Trip identifier for scoping day entries
 * @param {string[]} dateRange - Array of ISO date strings from accommodation stay period
 * @param {Object} daysService - Service object for accessing day entry operations
 * @returns {Promise<Object>} Analysis of missing vs existing days
 * @returns {string[]} returns.missingDates - Dates that don't have day entries
 * @returns {Object[]} returns.existingDays - Day entries that were found
 * @returns {boolean} returns.needsNotification - Whether user should be notified about missing days
 * @returns {boolean} returns.canCreateSuggestions - Whether the system can suggest creating missing days
 */
async function findMissingDaysForAccommodationDates(userId, tripId, dateRange, daysService) {
  console.log("=== FIND MISSING DAYS START ===");
  console.log("findMissingDaysForAccommodationDates called with:");
  console.log("  - userId:", userId);
  console.log("  - tripId:", tripId);
  console.log("  - dateRange:", JSON.stringify(dateRange));
  console.log("  - daysService type:", typeof daysService);
  console.log("  - daysService is mock:", daysService && daysService.isMockService);
  console.log("  - daysService methods available:", daysService ? Object.keys(daysService) : 'none');

  const result = {
    missingDates: [],
    existingDays: [],
    needsNotification: false,
    canCreateSuggestions: true
  };

  try {
    console.log("findMissingDaysForAccommodationDates: STEP 1 - Validating input parameters");
    if (!Array.isArray(dateRange) || dateRange.length === 0) {
      console.log("findMissingDaysForAccommodationDates: STEP 1 FAILED - Invalid dateRange:", dateRange);
      result.canCreateSuggestions = false;
      console.log("=== FIND MISSING DAYS END (INVALID INPUT) ===");
      return result;
    }
    console.log("findMissingDaysForAccommodationDates: STEP 1 SUCCESS - Input validation passed");

    console.log("findMissingDaysForAccommodationDates: STEP 2 - Checking each date for existing days");
    // Check each date for existing day
    for (const date of dateRange) {
      console.log(`findMissingDaysForAccommodationDates: STEP 2.${dateRange.indexOf(date) + 1} - Checking date: ${date}`);
      try {
        console.log(`findMissingDaysForAccommodationDates: Calling daysService.getDayByDate(${userId}, ${tripId}, ${date})`);
        const existingDay = await daysService.getDayByDate(userId, tripId, date);
        console.log(`findMissingDaysForAccommodationDates: getDayByDate result for ${date}:`, existingDay ? JSON.stringify(existingDay, null, 2) : 'null');

        if (existingDay) {
          console.log(`findMissingDaysForAccommodationDates: Found existing day for ${date} with ID: ${existingDay.id}`);
          const dayInfo = {
            date,
            dayId: existingDay.id,
            hasContent: Boolean(existingDay.content && Array.isArray(existingDay.content) && existingDay.content.length > 0)
          };
          console.log(`findMissingDaysForAccommodationDates: Adding to existingDays:`, JSON.stringify(dayInfo, null, 2));
          result.existingDays.push(dayInfo);
        } else {
          console.log(`findMissingDaysForAccommodationDates: No existing day found for ${date}, adding to missingDates`);
          result.missingDates.push(date);
        }
      } catch (error) {
        // If getDayByDate fails, treat as missing day
        console.warn(`findMissingDaysForAccommodationDates: Error checking day existence for ${date}:`, error);
        console.warn(`findMissingDaysForAccommodationDates: Error stack:`, error.stack);
        console.log(`findMissingDaysForAccommodationDates: Treating ${date} as missing due to error`);
        result.missingDates.push(date);
      }
    }

    console.log("findMissingDaysForAccommodationDates: STEP 3 - Analyzing results");
    console.log("findMissingDaysForAccommodationDates: STEP 3 - Result summary:");
    console.log("  - existingDays count:", result.existingDays.length);
    console.log("  - missingDates count:", result.missingDates.length);
    console.log("  - existingDays:", JSON.stringify(result.existingDays, null, 2));
    console.log("  - missingDates:", JSON.stringify(result.missingDates, null, 2));

    // Determine if user notification is needed when days are missing
    result.needsNotification = result.missingDates.length > 0;
    console.log("findMissingDaysForAccommodationDates: STEP 3 - needsNotification:", result.needsNotification);

    // Can create suggestions if at least some days exist or if service is available
    result.canCreateSuggestions = result.existingDays.length > 0 || result.missingDates.length < dateRange.length;
    console.log("findMissingDaysForAccommodationDates: STEP 3 - canCreateSuggestions:", result.canCreateSuggestions);

    console.log("findMissingDaysForAccommodationDates: FINAL RESULT:", JSON.stringify(result, null, 2));
    console.log("=== FIND MISSING DAYS END (SUCCESS) ===");

  } catch (error) {
    console.error('findMissingDaysForAccommodationDates: ERROR CAUGHT - Error in findMissingDaysForAccommodationDates:', error);
    console.error('findMissingDaysForAccommodationDates: ERROR STACK:', error.stack);
    // If service is completely unavailable, still allow suggestions but flag the issue
    result.missingDates = [...dateRange];
    result.needsNotification = true;
    result.canCreateSuggestions = false;
    console.log("findMissingDaysForAccommodationDates: ERROR RESULT:", JSON.stringify(result, null, 2));
    console.log("=== FIND MISSING DAYS END (ERROR) ===");
  }

  return result;
}

/**
 * Validates accommodation dates specifically for day entry creation workflows.
 * This function ensures that accommodation date information is suitable for generating
 * day entries in the user's daily itinerary, applying business rules and validation
 * logic specific to the day creation process.
 * 
 * The validation serves multiple purposes:
 * - Ensures data quality before attempting to create day entries
 * - Applies business rules about maximum stay durations and reasonable date ranges
 * - Provides user-friendly warnings about potentially problematic situations
 * - Classifies the type of day creation needed (single day vs multi-day)
 * 
 * Business rules enforced:
 * - Check-in date is mandatory for day creation (users need at least arrival information)
 * - Stay duration cannot exceed 90 days (prevents system performance issues)
 * - Long stays (7+ days) generate warnings to help users understand the scope
 * - Extended stays (30+ days) get additional warnings about complexity
 * 
 * The function leverages existing date validation logic while adding day-creation-specific
 * rules and enhanced error messaging appropriate for the day entry creation context.
 * 
 * @param {Object} accommodationDetails - Accommodation details object with date fields
 * @param {string} accommodationDetails.checkInDate - ISO date string for check-in (required)
 * @param {string} [accommodationDetails.checkOutDate] - ISO date string for check-out (optional)
 * @returns {Object} Validation result with day creation capability flags and detailed feedback
 * @returns {boolean} returns.isValid - Whether the dates pass validation
 * @returns {boolean} returns.canCreateDays - Whether day entries can be created from these dates
 * @returns {string} returns.stayType - Classification: 'SINGLE_DAY', 'SAME_DAY_CHECKIN_CHECKOUT', 'MULTI_DAY', or 'invalid' for errors
 * @returns {string[]} returns.errors - Array of error messages for validation failures
 * @returns {string[]} returns.warnings - Array of warning messages for potentially problematic situations
 */
function validateAccommodationDatesForDayCreation(accommodationDetails) {
  const result = {
    isValid: false,
    canCreateDays: false,
    stayType: 'invalid',
    errors: [],
    warnings: []
  };

  try {
    if (!accommodationDetails) {
      result.errors.push('Accommodation details are required');
      return result;
    }

    const checkInDate = accommodationDetails.checkInDate;
    const checkOutDate = accommodationDetails.checkOutDate;

    // Must have at least check-in date for day creation
    if (!checkInDate) {
      result.errors.push('Check-in date is required for day creation');
      return result;
    }

    // Use existing validation function for basic date validation
    const basicValidation = validateAccommodationDates(checkInDate, checkOutDate);
    if (!basicValidation.isValid) {
      result.errors.push(basicValidation.error);
      return result;
    }

    const checkIn = new Date(checkInDate);
    if (isNaN(checkIn.getTime())) {
      result.errors.push('Invalid check-in date format');
      return result;
    }

    // Single check-in date only
    if (!checkOutDate) {
      result.isValid = true;
      result.canCreateDays = true;
      result.stayType = 'SINGLE_DAY';
      result.warnings.push('Single day entry will be created for check-in date');
      return result;
    }

    // Check-in and check-out dates provided
    const checkOut = new Date(checkOutDate);
    if (isNaN(checkOut.getTime())) {
      result.errors.push('Invalid check-out date format');
      return result;
    }

    if (checkOut < checkIn) {
      result.errors.push('Check-out date must be after check-in date');
      return result;
    }

    // Calculate duration for warnings and limits
    const daysDiff = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) {
      // Same day check-in and check-out
      result.isValid = true;
      result.canCreateDays = true;
      result.stayType = 'SAME_DAY_CHECKIN_CHECKOUT';
      result.warnings.push('Same-day check-in and check-out detected');
      return result;
    }

    // Warning thresholds
    if (daysDiff > 7) {
      result.warnings.push(`Long stay detected (${daysDiff} days). Multiple day entries will be created.`);
    }

    if (daysDiff > 30) {
      result.warnings.push(`Extended stay detected: ${daysDiff} days. Consider breaking into shorter periods.`);
    }

    // Business rule: Maximum stay duration
    if (daysDiff > 90) {
      result.errors.push('Stay duration exceeds maximum allowed (90 days)');
      return result;
    }

    result.isValid = true;
    result.canCreateDays = true;
    result.stayType = 'MULTI_DAY';

  } catch (error) {
    result.errors.push(`Validation error: ${error.message}`);
  }

  return result;
}

// ====================================================================
// ACCOMMODATION-DAYS CONTENT FORMATTING UTILITIES
// ====================================================================
// The following functions implement content formatting standards for accommodation-day
// integration. They handle content generation, formatting, and integration while
// maintaining consistency with existing patterns.

/**
 * Generates formatted day entry content for accommodation suggestions.
 * This function creates standardized text content that can be added to day entries
 * to represent accommodation stays, handling missing information gracefully and
 * applying contextual formatting based on the role of each day in the stay.
 * 
 * The content generation follows a hierarchical approach to handle missing data:
 * 1. Use accommodation name if available
 * 2. Fall back to accommodation summary if name is missing
 * 3. Use generic placeholder if both name and summary are unavailable
 * 4. Add city information when available for location context
 * 5. Apply contextual labels (Check-in, Check-out) when appropriate
 * 
 * Content formatting examples:
 * - "Stay at Grand Hotel: Paris, France (Check-in)" - arrival day with full info
 * - "Stay at Grand Hotel: Paris, France" - regular staying day
 * - "Stay at Grand Hotel: Paris, France (Check-out)" - departure day
 * - "Stay at Grand Hotel" - missing city information
 * - "Stay at accommodation" - missing name and city information
 * 
 * The function is designed to integrate with place-based accommodation data,
 * where accommodation city information may be linked to place entities rather
 * than stored as simple text fields.
 * 
 * @param {Object} accommodation - The accommodation object containing details
 * @param {Object} accommodation.details - Accommodation details object
 * @param {string} [accommodation.details.name] - Accommodation name
 * @param {string} [accommodation.details.city] - City or location information
 * @param {string} [accommodation.summary] - Accommodation summary text
 * @param {Object} [accommodation.placeReference] - Reference to linked place entity
 * @param {string} date - ISO date string for the day being processed
 * @param {string[]} allDatesInRange - Array of all dates in the accommodation stay period
 * @returns {string} Formatted content text ready for day entry integration
 */
function generateDayContentForAccommodationSuggestions(accommodation, date, allDatesInRange) {
  try {
    // Extract accommodation details with fallback hierarchy
    const accommodationName = accommodation?.details?.name ||
      accommodation?.summary ||
      'Accommodation';

    const city = accommodation?.details?.city;

    // Use utility function to determine stay context
    const context = determineAccommodationStayContext(accommodation?.details, date, allDatesInRange);

    // Build location string with proper fallbacks
    let locationString = '';
    if (city) {
      // Check if accommodation is linked to a place for enhanced city display
      if (accommodation?.placeReference?.placeId) {
        // Use place data if available (ready for place service integration)
        locationString = city;
      } else {
        locationString = city;
      }
    }

    // Format content using standardized templates
    const baseContent = locationString
      ? `Stay at ${accommodationName}: ${locationString}`
      : `Stay at ${accommodationName}`;

    // Add context indicators based on stay type
    switch (context) {
      case 'check-in':
        return `${baseContent} (Check-in)`;
      case 'check-out':
        return `${baseContent} (Check-out)`;
      case 'staying':
        return baseContent;
      case 'single-day':
      default:
        return baseContent;
    }
  } catch (error) {
    console.error('Error generating day content for accommodation suggestions:', error);
    // Return safe fallback
    return `Stay at ${accommodation?.details?.name || 'Accommodation'}`;
  }
}

/**
 * Formats complete day content by integrating accommodation entries with existing day content.
 * This function handles the complex process of merging new accommodation information into
 * existing day entries while preserving user-created content and maintaining proper formatting.
 * 
 * The integration process addresses several challenges:
 * - Preserving existing user-written day content without overwriting
 * - Preventing duplicate accommodation entries when content already exists
 * - Maintaining consistent formatting and spacing for readability
 * - Handling various edge cases like empty content, whitespace, and malformed data
 * 
 * Content integration strategy:
 * 1. Detect if accommodation content already exists in the day entry
 * 2. If duplicate detected, return existing content unchanged
 * 3. If no duplicate, append accommodation content with proper spacing
 * 4. Handle empty/null existing content by using accommodation content as base
 * 5. Ensure proper line breaks and formatting for multi-line content
 * 
 * The function uses intelligent duplicate detection to avoid creating redundant entries
 * when the same accommodation information is already present in the day content,
 * which can happen during repeated suggestion processing or user interactions.
 * 
 * Formatting examples:
 * - Empty day + accommodation → "Stay at Hotel: Paris"
 * - "Visit museum" + accommodation → "Visit museum\nStay at Hotel: Paris"
 * - "Stay at Hotel: Paris" + same accommodation → "Stay at Hotel: Paris" (no duplicate)
 * 
 * @param {string} existingDayContent - Current content of the day entry (may be empty/null)
 * @param {string} accommodationContent - New accommodation content to integrate
 * @returns {string} Complete day content with accommodation entry properly integrated
 */
function formatDayContentWithAccommodationEntry(existingDayContent, accommodationContent) {
  try {
    // Handle empty/null content gracefully
    if (!accommodationContent || accommodationContent.trim() === '') {
      return existingDayContent || '';
    }

    if (!existingDayContent || existingDayContent.trim() === '') {
      return accommodationContent;
    }

    // Check for duplicate accommodation content (prevent double-adding)
    const existingLines = existingDayContent.split('\n');

    // Extract accommodation name from new content for duplicate detection
    const accommodationNameMatch = accommodationContent.match(/Stay at ([^:]+)/);
    if (accommodationNameMatch) {
      const accommodationName = accommodationNameMatch[1].trim();
      const accommodationPattern = new RegExp(
        `^Stay at.*${accommodationName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
        'i'
      );

      const duplicateExists = existingLines.some(line => accommodationPattern.test(line));

      if (duplicateExists) {
        return existingDayContent; // No change needed
      }
    }

    // Determine optimal placement strategy
    // Strategy: Add accommodation entries at the end (after activities and other content)
    const cleanExistingContent = existingDayContent.trim();

    // Format with proper line breaks
    return `${cleanExistingContent}\n${accommodationContent}`;
  } catch (error) {
    console.error('Error formatting day content with accommodation entry:', error);
    // Return safe fallback - preserve existing content
    return existingDayContent || accommodationContent || '';
  }
}

/**
 * Calculates the overall date range covered by the user's Days list
 * @param {Array} days - Array of day objects from daysService
 * @returns {Object|null} Date range object with startDate and endDate, or null if no days with dates
 */
function calculateDaysListDateRange(days) {
  if (!days || days.length === 0) {
    return null;
  }

  // Filter days that have actual dates (not null)
  const daysWithDates = days.filter(day => day.date && day.date.trim());

  if (daysWithDates.length === 0) {
    return null;
  }

  // Get all dates and sort them
  const dates = daysWithDates.map(day => day.date).sort();

  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1],
    totalDays: dates.length
  };
}

/**
 * Calculates trip start and end dates from an array of days
 * This is used to persist trip dates to Firestore based on the days' date range
 * @param {Array} days - Array of day objects from daysService
 * @returns {Object} - { startDate: string|null, endDate: string|null }
 */
function calculateTripDatesFromDays(days) {
  if (!days || days.length === 0) {
    return { startDate: null, endDate: null };
  }

  // Filter days that have actual dates (not null)
  const daysWithDates = days.filter(day => day.date && day.date.trim());

  if (daysWithDates.length === 0) {
    return { startDate: null, endDate: null };
  }

  // Get all dates and sort them
  const dates = daysWithDates.map(day => day.date).sort();

  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1]
  };
}

/**
 * Formats a date string for display in user-friendly format
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @returns {string} Formatted date string (e.g., "4 August 2025")
 */
function formatDateForDisplay(dateString) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid
    }

    // Format as "4 August 2025" using manual formatting
    const day = date.getDate();
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  } catch (error) {
    console.warn('Error formatting date for display:', error);
    return dateString; // Return original string if formatting fails
  }
}

export {
  calculateAccommodationDateRange,
  formatAccommodationContentForDay,
  validateAccommodationDates,
  accommodationContentExists,
  extractAccommodationReferences,
  removeAccommodationContentFromDay,
  generateAccommodationDayLinkId,
  analyzeDateRangeChanges,
  isContractionScenario,
  isExpansionScenario,
  analyzeShiftScenario,
  generateDateChangeSuggestions,
  validateDateChanges,
  // Day entry creation utilities
  calculateAccommodationDateRangeForDaysSuggestions,
  determineAccommodationStayContext,
  findMissingDaysForAccommodationDates,
  validateAccommodationDatesForDayCreation,
  // Content formatting utilities
  generateDayContentForAccommodationSuggestions,
  formatDayContentWithAccommodationEntry,
  calculateDaysListDateRange,
  calculateTripDatesFromDays,
  formatDateForDisplay
}; 